import { useEffect, useMemo, useRef, useState } from 'react';
import { IRecipientForList } from '@nkr/models/index';
import { MatchStatus, PatientType } from '@nkr/enums/index';
import { BodyScrollEvent, ColDef, FirstDataRenderedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Glyphicon, Label } from 'react-bootstrap';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { GridWrapper } from './styles';
import { Utility } from '@nkr/services/utility';
import { RecipientActions } from './actions';
import OverlayMenuButton, { IMenuItem } from '@nkr/components/overlay-menu-button';

interface IProps {
    centerId: number,
    data: IRecipientForList[],
    centerHasRecipientShield: boolean,
}

const Grid = (props: IProps) => {
    const [menuDisplayed, setMenuDisplayed] = useState(false);

    const localDataRef = useRef(null);

    useEffect(() => {
        localDataRef.current = props.data;
    }, [props.data]);

    const onGridReady = (e: GridReadyEvent) => {
        e.api.setSortModel([
            {
                colId: 'isExportable',
                sort: 'asc'
            }
        ]);

        e.api.sizeColumnsToFit();
    }

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onSortChanged = (e: SortChangedEvent) => {
        e.api.refreshCells();
    };

    const onBodyScroll = (e: BodyScrollEvent) => {
        if (menuDisplayed) {
            document.body.click();
            setMenuDisplayed(false);
        }
    };

    const handleMenuToggle = (show: boolean) => {
        setMenuDisplayed(show);
    }

    const handleAction = (recipientId: number, action: RecipientActions) => {
        let centerId = props.centerId > 0 ? props.centerId : 0;

        if (centerId == 0) {
            let d = localDataRef.current.find(x => x.id == recipientId);
            centerId = d.centerId;
        }

        let href = '';

        if (action == RecipientActions.Edit) {
            href = '/center/' + centerId + '/recipient/' + recipientId + '/profile/general';
        }

        if (action == RecipientActions.PairedDonors) {
            href = `center/${centerId}/donors/swap?center=${centerId}&status[]=active&status[]=inactive&status[]=donated&type[]=bridge&type[]=dd&type[]=paired&dob=`;
        }

        if (action == RecipientActions.Toolbox) {
            href = `center/${centerId}/recipient/${recipientId}/toolbox`;
        }

        if (action == RecipientActions.PotentialDonors) {
            href = '/center/' + centerId + '/recipient/' + recipientId + '/potential-donors';
        }

        if (action == RecipientActions.Order3gTissueTyping) {
            href = '/center/' + centerId + '/cheek-swab-request/recipient/' + recipientId;
        }

        if (action == RecipientActions.UtilizeBulkCheekSwab) {
            href = __OUTSIDEURL__ + '/centers/histogenetics/bulk-recipient-cheek-swab.php?recpId=' + recipientId;
        }

        window.open(href, '_blank');
    };

    const columns: ColDef[] = [
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>R#</SortableHeaderComponent>,
            headerName: 'R#',
            field: 'isLeadRetrieved',
            width: 70,
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
            headerName: 'Center',
            field: 'centerAlias',
            width: 130,
            hide: props.centerId > 0
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Type</SortableHeaderComponent>,
            headerName: 'Type',
            field: 'patientTypeId',
            width: 85,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRecipientForList;
                let style = 'info';

                if (data.patientTypeId == PatientType.KidneyReplacementCase) {
                    style = 'danger';
                }

                if (data.patientTypeId == PatientType.Paired) {
                    style = 'default';
                }

                if (data.patientTypeId == PatientType.Waitlist) {
                    style = 'warning';
                }

                if (data.patientTypeId == PatientType.StandardVoucherHolder || data.patientTypeId == PatientType.FamilyVoucherHolder) {
                    style = 'success';
                }

                return <Label bsStyle={style}>{data.patientTypeAbbr}</Label>;
            }
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Registered</SortableHeaderComponent>,
            headerName: 'Registered',
            field: 'registeredDate',
            width: 110,
            comparator: Utility.dateComparator
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Alias</SortableHeaderComponent>,
            headerName: 'Alias',
            field: 'alias',
            width: 130,
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Gen</SortableHeaderComponent>,
            headerName: 'Gen',
            field: 'gender',
            width: 50,
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Age</SortableHeaderComponent>,
            headerName: 'Age',
            field: 'age',
            width: 50,
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Rec<br />ABO</SortableHeaderComponent>,
            headerName: 'Rec ABO',
            field: 'bloodType',
            width: 50,
            sortable: false
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Int.<br />Donor<br />ABO</SortableHeaderComponent>,
            headerName: 'Int. Donor ABO',
            field: 'donorBloodTypes',
            width: 60,
            cellRenderer: (params: ICellRendererParams) => params.value,
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>High<br />Res<br />Typing</SortableHeaderComponent>,
            headerName: 'High Res Typing',
            field: 'has3Typing',
            width: 70,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRecipientForList;
                return (
                    <div style={{ paddingTop: 3 }}>{data.has3Typing ? <Glyphicon glyph='ok-sign' /> : null}</div>
                );
            },
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Avoids</SortableHeaderComponent>,
            headerName: 'Avoids',
            field: 'avoids',
            width: 65,
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>NKR<br />cPRA4</SortableHeaderComponent>,
            headerName: 'NKR cPRA4',
            field: 'cpra4',
            width: 75,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRecipientForList;
                return data.cpra4.toFixed(2);
            },
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>PMP</SortableHeaderComponent>,
            headerName: 'Pair Match Power',
            field: 'pairMatchPower',
            width: 60,
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Decline<br />Ratio</SortableHeaderComponent>,
            headerName: 'Decline Ratio',
            field: 'declineRatio',
            width: 75,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRecipientForList;
                return <>{(data.declineRatio * 100.0).toFixed(0)}%</>;
            },
        },
        // {
        //     headerName: 'Wait Fwt',
        //     field: 'recipientFwt',
        //     width: 75,
        //     headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}><div style={{ fontSize: 9 }}>Forecasted</div>Wait<br />(months)</SortableHeaderComponent>,
        //     cellRendererFramework: (params: ICellRendererParams) => {
        //         let data = params.data as IRecipientForList;

        //         if (![PatientType.FamilyVoucherHolder, PatientType.StandardVoucherHolder, PatientType.KidneyReplacementCase, PatientType.Paired].includes(data.patientTypeId)){
        //             return 'N/A';
        //         }

        //         if (data.recipientFwt > 6) {
        //             return (
        //                 <span style={{ color: 'red' }}>{data.recipientFwt}

        //                     {data.recipientFwt == 60 ? "+" : ""}

        //                 </span>
        //             );
        //         }

        //         return <>{data.recipientFwt}</>
        //     },
        // },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Wait<br />Est.<br /><span style={{ fontSize: 9 }}>(months)</span></SortableHeaderComponent>,
            headerName: 'Wait Est.',
            field: 'estimatedWaitTimeValue',
            width: 70,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRecipientForList;

                if (data.estimatedWaitTime > 6) {
                    return (
                        <span style={{ color: 'red' }}>{data.estimatedWaitTime}</span>
                    );
                }

                return <>{data.estimatedWaitTime}</>
            },
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Wait<br />Actual<br /><span style={{ fontSize: 9 }}>(months)</span></SortableHeaderComponent>,
            headerName: 'Wait Actual',
            field: 'actualWaitTime',
            width: 70,
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Wait<br />Var.<br /><span style={{ fontSize: 9 }}>(months)</span></SortableHeaderComponent>,
            headerName: 'Wait Var.',
            field: 'waitTimeVarianceValue',
            width: 70,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRecipientForList;

                if (data.waitTimeVariance < 0) {
                    return <span style={{ color: 'red' }}>+{Math.abs(data.waitTimeVariance)}</span>
                } else {
                    return <span style={{ color: 'green' }}>-{Math.abs(data.waitTimeVariance)}</span>
                }
            }
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>TXP'd</SortableHeaderComponent>,
            headerName: 'Transplanted',
            field: 'transplantDate',
            width: 90,
            comparator: Utility.dateComparator,
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Status</SortableHeaderComponent>,
            headerName: 'Status',
            field: 'matchStatus',
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Exportability</SortableHeaderComponent>,
            headerName: 'Exportability',
            field: 'isExportable',
            width: 135,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRecipientForList;
                let style = 'info';
                let title = '';
                let label = '';

                if (!data.isActive && data.matchStatusId == MatchStatus.NeedsMatch) {
                    style = 'default';
                    title = 'Patient is inactive and will not be matched';
                    label = 'Inactive';
                } else if (data.matchStatusId > MatchStatus.NeedsMatch) {
                    if (data.matchStatusId == MatchStatus.Transplanted) {
                        style = 'primary';
                        title = 'Patient is transplanted';
                        label = 'Transplanted';
                    } else {
                        style = 'info';
                        title = 'Patient is already in a match';
                        label = 'In Match';
                    }
                } else {
                    if (data.isExportable) {
                        style = 'success';
                        title = 'Patient is active and all required data is completed to receive a match offer';
                        label = 'Matchable';
                    } else {
                        style = 'danger';
                        title = 'Patient is active, but NOT all data required to receive a match offer has been entered';
                        label = 'Incomplete';
                    }
                }

                return <Label bsStyle={style} title={title}>{label}</Label>;
            },
            comparator: (valueA: any, valueB: any, nodeA: any, nodeB: any) => {
                const getLabel = (data: IRecipientForList) => {
                    if (!data.isActive && data.matchStatusId === MatchStatus.NeedsMatch) return 'Inactive';
                    if (data.matchStatusId === MatchStatus.Transplanted) return 'Transplanted';
                    if (data.matchStatusId > MatchStatus.NeedsMatch) return 'In Match';
                    return data.isExportable ? 'Matchable' : 'Incomplete';
                };

                return getLabel(nodeA.data).localeCompare(getLabel(nodeB.data));
            }
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient<br />Shield<br />Eligible</SortableHeaderComponent>,
            headerName: 'Recipient Shield Eligible',
            width: 85,
            hide: !props.centerHasRecipientShield,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRecipientForList;

                return (
                    <div style={{ paddingTop: 3 }}>{data.hasMedicareAb ? <Glyphicon glyph='ok-sign' /> : null}</div>
                );
            },
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Microsite</SortableHeaderComponent>,
            headerName: 'Microsite',
            field: 'micrositeRecipientId',
            width: 85,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRecipientForList;

                return (
                    <div style={{ paddingTop: 3 }}>{data.micrositeRecipientId > 0 ? <Glyphicon glyph='ok-sign' /> : null}</div>
                );
            },
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Pending <br />Preselects</SortableHeaderComponent>,
            headerName: 'Pending Preselects',
            field: 'pendingBioMatchDonors',
            width: 125,
            hide: true
        },
        {
            headerName: '',
            width: 95,
            cellClass: 'action-menu',
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRecipientForList;

                let menu: IMenuItem[] = [
                    { id: data.id, action: RecipientActions.Edit, include: true, selectable: true, element: <>Edit</> },
                    { id: data.id, action: RecipientActions.PairedDonors, include: true, selectable: true, element: <>Paired Donors</> },
                    { id: data.id, action: RecipientActions.Toolbox, include: true, selectable: true, element: <>Toolbox</> },
                    { id: data.id, action: RecipientActions.PotentialDonors, include: true, selectable: true, element: <>Pre-Selects</> },
                    { id: data.id, action: RecipientActions.Order3gTissueTyping, include: true, selectable: true, element: <>Order High Res Tissue Typing (Cheek Swab)</> },
                    { id: data.id, action: RecipientActions.UtilizeBulkCheekSwab, include: true, selectable: true, element: <>Utilize a Bulk Cheek Swab (Kit on hand)</> },
                ];

                if (data.hasMedicareAb && data.centerHasRecipientShield) {
                    if (data.recipientShieldEnrolled) {
                        menu.push({ id: data.id, action: RecipientActions.EnrollRecipientShield, include: true, selectable: false, element: <>Enroll Recipient Shield (Already Enrolled)</> })
                    } else {
                        menu.push({ id: data.id, action: RecipientActions.EnrollRecipientShield, include: true, selectable: true, element: <>Enroll Recipient Shield</> })
                    }
                }

                return (
                    <OverlayMenuButton
                        bsStyle='primary'
                        bsSize='xs'
                        title='Actions'
                        menu={menu}
                        includeCaret={true}
                        onToggle={handleMenuToggle}
                        onSelect={(id, action) => handleAction(id, action)}
                    />
                );
            }
        }
    ]

    const columnDefs = useMemo(() => columns, []);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <GridWrapper className='ag-theme-bootstrap'>
            <AgGridReact
                defaultColDef={{
                    cellClass: 'list-cell',
                    headerClass: 'list-header',
                    sortable: true,
                    suppressMenu: true
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={54}
                groupHeaderHeight={30}
                rowHeight={36}
                suppressRowClickSelection={false}
                suppressCellSelection={true}
                enableCellTextSelection={true}
                suppressContextMenu={true}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onSortChanged={onSortChanged}
                onBodyScroll={onBodyScroll}
            />
        </GridWrapper>
    );
}

export default Grid;